import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Loader } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, BadgeColor } from '@/components/ui/catalyst/badge';
import { Button as CatalystButton } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Card } from '@/components/ui/v3/Card';
import { Divider } from '@/components/ui/v3/Divider';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { useMatter } from '@/hooks/useMatter';
import { TreatmentRecordRequestSchema } from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { useDeleteMedicalProviderMutation } from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import { OpenInDirectoryButton } from '../../directory';
import { IdentityStatusBadge } from '../../medical-provider/IdentityStatusBadge';
import { RequestCardWithTabs } from '../request/cards/RequestCard';
import { CreateProviderRequestDialog } from '../request/modals/CreateRecordRequestModal';
import { RequestsSection } from '../treatment/TreatmentProvidersSection';
import {
  formatProviderTreatmentDates,
  getProviderTreatmentDates,
} from '../utils';

interface ProviderOverviewInformationProps {
  provider: MedicalProvider;
}

export const ProviderOverviewSubheading = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => {
  return (
    <div className="flex flex-row items-center gap-2">
      <span className="text-xs font-semibold text-gray-500">{label}</span>
      <span className="text-xs text-gray-500 truncate">{value}</span>
    </div>
  );
};

export const ProviderStatusBadge = ({
  treatmentStatus,
}: {
  treatmentStatus: ProviderTreatmentStatus;
}) => {
  const statusToColor = (status: ProviderTreatmentStatus): BadgeColor => {
    switch (status) {
      case ProviderTreatmentStatus.TREATING:
        return 'purple';
      case ProviderTreatmentStatus.COMPLETED:
        return 'green';
      case ProviderTreatmentStatus.REFERRED_TO:
        return 'blue';
      case ProviderTreatmentStatus.DEVIATING:
        return 'red';
      default:
        return 'zinc';
    }
  };

  return (
    <Badge color={statusToColor(treatmentStatus)}>{treatmentStatus}</Badge>
  );
};

export const ProviderOverviewInformation = ({
  provider,
}: ProviderOverviewInformationProps) => {
  const { matterId } = useMatter();
  const { startingDate, endingDate } = useMemo(
    () => getProviderTreatmentDates(provider),
    [provider],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">Treatment Status: </span>
        <ProviderStatusBadge treatmentStatus={provider.treatment_status} />
        <IdentityStatusBadge
          status={provider.identity_status}
          matterId={matterId}
          providerId={provider.id}
          identityDueAt={provider.identity_due_at}
          identityResolvedAt={provider.identity_resolved_at}
          identityDeferReason={provider.identity_defer_reason}
        />
      </div>
      <div className="flex flex-row gap-6 flex-wrap">
        <ProviderOverviewSubheading
          label="Dates:"
          value={formatProviderTreatmentDates(
            provider.treatment_status,
            startingDate,
            endingDate,
          )}
        />
        <ProviderOverviewSubheading
          label="Number of Visits:"
          value={provider.encounters?.length?.toString() ?? 'Unknown'}
        />

        <ProviderOverviewSubheading
          label="Location:"
          value={provider.address ?? 'Not on file'}
        />
      </div>
    </div>
  );
};

const DeleteProviderDialog = ({
  isOpen,
  setIsOpen,
  providerId,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  providerId: string;
}) => {
  const { matterId } = useMatter();
  const [deleteProvider, { isLoading }] = useDeleteMedicalProviderMutation();

  const handleDeleteProvider = async () => {
    deleteProvider({ matterId, providerId })
      .then(() => {
        setIsOpen(false);
        toast.success('Provider deleted successfully');
      })
      .catch(() => {
        toast.error('Failed to delete provider');
      });
  };

  return (
    <Dialog open={isOpen} onClose={setIsOpen}>
      <DialogTitle>Delete provider</DialogTitle>
      <DialogDescription>
        Are you sure you want to delete this provider? This action cannot be
        undone.
      </DialogDescription>
      <DialogActions>
        <CatalystButton plain onClick={() => setIsOpen(false)}>
          Cancel
        </CatalystButton>
        <CatalystButton
          color="red"
          onClick={handleDeleteProvider}
          className="flex flex-row items-center gap-2"
        >
          {isLoading && <Loader className="animate-spin h-4 w-4" />} Delete
        </CatalystButton>
      </DialogActions>
    </Dialog>
  );
};

const ProviderOverviewHeader = ({
  provider,
}: {
  provider: MedicalProvider;
}) => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const [isDeleteProviderDialogOpen, setIsDeleteProviderDialogOpen] =
    useState(false);

  const handleEditProvider = () => {
    navigate(`/matters/${matterId}/treatment/${provider.id}/edit`);
  };

  const handleViewProvider = () => {
    navigate(`/matters/${matterId}/treatment/${provider.id}`);
  };

  const handleDeleteProvider = () => {
    setIsDeleteProviderDialogOpen(true);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row gap-2 justify-between items-center">
        <div className="flex items-center gap-2 max-w-[70%]">
          <Tooltip
            content={`View details for ${provider.name || 'Unnamed Provider'}`}
            variant="dark"
            side="top"
          >
            <button
              type="button"
              onClick={handleViewProvider}
              className="text-md font-semibold hover:text-gray-700 cursor-pointer underline decoration-dotted underline-offset-4 text-left truncate"
            >
              {provider.name || 'Unnamed Provider'}
            </button>
          </Tooltip>
        </div>
        <div className="flex flex-row gap-2 items-center">
          <div className="flex flex-row gap-1">
            <CatalystButton plain onClick={handleViewProvider}>
              <EyeIcon className="w-6 h-6" />
            </CatalystButton>
            <OpenInDirectoryButton
              providerServiceRefId={provider.provider_service_ref_id}
            />
            <CatalystButton plain onClick={handleEditProvider}>
              <PencilIcon className="w-6 h-6" />
            </CatalystButton>
            <CatalystButton plain onClick={handleDeleteProvider}>
              <TrashIcon className="w-6 h-6" />
            </CatalystButton>
          </div>
        </div>
      </div>
      <span className="text-sm text-gray-500">{provider.description}</span>
      <DeleteProviderDialog
        isOpen={isDeleteProviderDialogOpen}
        setIsOpen={setIsDeleteProviderDialogOpen}
        providerId={provider.id}
      />
    </div>
  );
};

export const ProviderRequestsSkeleton = () => {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/3" />
      <div className="h-20 bg-gray-100 rounded" />
    </div>
  );
};

// Old design: RequestCardWithTabs list
interface ProviderRequestsProps {
  requests: TreatmentRecordRequestSchema[];
  provider: MedicalProvider;
  isLoading: boolean;
}

const ProviderRequests = ({
  requests,
  provider,
  isLoading,
}: ProviderRequestsProps) => {
  if (isLoading) {
    return <ProviderRequestsSkeleton />;
  }

  return (
    <div className="space-y-4">
      {requests.map((request, index) => (
        <div key={request.id}>
          {index > 0 && <Divider className="mb-4" />}
          <RequestCardWithTabs request={request} provider={provider} />
        </div>
      ))}
    </div>
  );
};

// Old design: Add Request button
const ProviderRequestActions = ({
  provider,
}: {
  provider: MedicalProvider;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleAddRequest = () => {
    setIsOpen(true);
  };
  return (
    <>
      <div className="flex flex-row gap-2 items-center justify-end">
        <CatalystButton outline onClick={handleAddRequest}>
          <PlusIcon className="w-4 h-4" />
          Add Request
        </CatalystButton>
      </div>
      <CreateProviderRequestDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        provider={provider}
      />
    </>
  );
};

interface ProviderOverviewProps {
  provider: MedicalProvider;
  isActive: boolean;
  requestId?: string; // limit to just this request
  /** Use new RequestRow design instead of RequestCardWithTabs */
  useNewDesign?: boolean;
}

export const ProviderOverview = React.forwardRef<
  HTMLDivElement,
  ProviderOverviewProps
>(({ provider, isActive, requestId, useNewDesign = true }, ref) => {
  const { matterId } = useMatter();
  const [isCreateRequestModalOpen, setIsCreateRequestModalOpen] =
    useState(false);

  const { data: requests, isLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsListQuery(
      {
        matterId,
        providerId: provider.id,
      },
      { pollingInterval: 5_000, skipPollingIfUnfocused: true },
    );
  const filteredRequests = useMemo(() => {
    if (!requestId) return requests;
    if (!requests) return [];
    return requests.filter((request) => request.id === requestId);
  }, [requests, requestId]);

  const handleNewRequest = () => {
    setIsCreateRequestModalOpen(true);
  };

  // New design: RequestsSection with RequestRow UI
  if (useNewDesign) {
    return (
      <>
        <Card className="flex flex-col gap-8" ref={ref} highlight={isActive}>
          <ProviderOverviewHeader provider={provider} />
          <ProviderOverviewInformation provider={provider} />
          {!isLoading && (
            <RequestsSection
              requests={filteredRequests}
              provider={provider}
              matterId={matterId}
              onNewRequest={handleNewRequest}
            />
          )}
          {isLoading && <ProviderRequestsSkeleton />}
        </Card>
        <CreateProviderRequestDialog
          isOpen={isCreateRequestModalOpen}
          setIsOpen={setIsCreateRequestModalOpen}
          provider={provider}
        />
      </>
    );
  }

  // Old design: RequestCardWithTabs
  return (
    <Card className="flex flex-col gap-8" ref={ref} highlight={isActive}>
      <ProviderOverviewHeader provider={provider} />
      <ProviderOverviewInformation provider={provider} />
      {filteredRequests?.length !== undefined &&
        filteredRequests.length > 0 &&
        !isLoading && (
          <ProviderRequests
            requests={filteredRequests}
            isLoading={isLoading}
            provider={provider}
          />
        )}
      <Divider />
      <ProviderRequestActions provider={provider} />
    </Card>
  );
});

ProviderOverview.displayName = 'ProviderOverview';
